import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { API } from '../../config';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';

const AdminOrders = () => {
    const [orders, setOrders] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchOrders();
        fetchProducts();
    }, []);

    const fetchProducts = async () => {
        try {
            const response = await axios.get(`${API}/products`);
            setProducts(response.data);
        } catch (error) {
            setProducts([]);
        }
    };

    useEffect(() => {
        fetchOrders();
    }, []);

    const fetchOrders = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API}/orders`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setOrders(response.data);
        } catch (error) {
            console.error('Failed to fetch orders:', error);
        } finally {
            setLoading(false);
        }
    };

    const updateOrderStatus = async (orderId, status) => {
        try {
            const token = localStorage.getItem('token');
            await axios.put(`${API}/orders/${orderId}/status?status=${status}`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Order status updated!');
            fetchOrders();
        } catch (error) {
            toast.error('Failed to update order status');
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'preparing': return 'bg-yellow-100 text-yellow-800';
            case 'dispatched': return 'bg-blue-100 text-blue-800';
            case 'completed': return 'bg-green-100 text-green-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const downloadDesign = (url, filename) => {
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success('Download started!');
    };

    const downloadAllDesigns = async (designs, orderId) => {
        for (const design of designs) {
            downloadDesign(design.imageUrl, `${orderId}-${design.pageName}.png`);
            await new Promise(resolve => setTimeout(resolve, 500));
        }
        toast.success(`All ${designs.length} designs downloaded!`);
    };

    if (loading) {
        return <div className="py-12 text-center text-gray-500 text-lg">Loading orders...</div>;
    }

    if (!orders || orders.length === 0) {
        return <div className="py-12 text-center text-gray-500 text-lg">No orders found.</div>;
    }

    return (
        <div className="bg-white rounded-3xl shadow-lg p-8">
            <div className="flex justify-between items-center mb-8">
                <h2 className="text-2xl font-bold text-gray-800">Orders Management</h2>
                <div className="text-sm text-gray-600 font-medium bg-gray-100 px-4 py-2 rounded-full">
                    Total Orders: {orders.length}
                </div>
            </div>
            <div className="overflow-hidden rounded-2xl border border-gray-100">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Image</th>
                            <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Order ID</th>
                            <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Customer</th>
                            <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Amount</th>
                            <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Address</th>
                            <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Contact</th>
                            <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Payment</th>
                            <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Qty</th>
                            <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Size</th>
                            <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Custom Designs</th>
                            <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Status</th>
                            <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                        {orders.map((order) => {
                            // Show a row for each item in the order
                            return order.items && order.items.length > 0
                                ? order.items.map((item, idx) => (
                                    <tr key={order.id + '-' + idx} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {(() => {
                                                // Find product by product_id
                                                const product = products.find(p => p.id === item.product_id || p._id === item.product_id);
                                                const imgSrc = product?.images?.[0] || 'https://via.placeholder.com/60x60?text=No+Image';
                                                return <img src={imgSrc} alt={item.product_title} className="h-12 w-12 object-cover rounded-lg shadow-sm" />;
                                            })()}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">#{order.id.slice(-6)}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{order.name || order.customer_name || order.user?.name || 'N/A'}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">₹{order.amount || order.total_amount || 'N/A'}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 max-w-[150px] truncate" title={order.billing_address}>{order.billing_address || 'N/A'}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{order.phone || order.contact || 'N/A'}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            <span className={`px-2 py-1 rounded-full text-xs font-bold ${order.payment_method === 'online' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}`}>
                                                {order.payment_method === 'online' ? 'ONLINE' : 'COD'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.quantity || 'N/A'}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.size || 'N/A'}</td>
                                        <td className="px-6 py-4">
                                            {item.custom_designs && item.custom_designs.length > 0 ? (
                                                <div className="flex flex-col gap-2">
                                                    <span className="text-xs font-bold text-green-600">
                                                        {item.custom_designs.length} page(s)
                                                    </span>
                                                    <div className="flex flex-wrap gap-1">
                                                        {item.custom_designs.map((design, dIdx) => (
                                                            <button
                                                                key={dIdx}
                                                                onClick={() => downloadDesign(design.imageUrl, `${order.id}-${design.pageName}.png`)}
                                                                className="text-xs bg-blue-100 hover:bg-blue-200 text-blue-800 px-2 py-1 rounded flex items-center gap-1 transition-colors"
                                                                title={`Download ${design.pageName}`}
                                                            >
                                                                <ArrowDownTrayIcon className="h-3 w-3" />
                                                                {design.pageName}
                                                            </button>
                                                        ))}
                                                    </div>
                                                    {item.custom_designs.length > 1 && (
                                                        <button
                                                            onClick={() => downloadAllDesigns(item.custom_designs, order.id)}
                                                            className="text-xs bg-green-600 hover:bg-green-700 text-white px-2 py-1 rounded flex items-center gap-1 transition-colors w-fit"
                                                        >
                                                            <ArrowDownTrayIcon className="h-3 w-3" />
                                                            Download All
                                                        </button>
                                                    )}
                                                </div>
                                            ) : (
                                                <span className="text-xs text-gray-400">No designs</span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`inline-flex px-3 py-1 text-xs font-bold rounded-full ${getStatusColor(order.status)}`}>
                                                {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                            {idx === 0 && (
                                                <select
                                                    value={order.status}
                                                    onChange={e => updateOrderStatus(order.id, e.target.value)}
                                                    className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-[#E31E24] focus:border-[#E31E24] bg-white shadow-sm"
                                                >
                                                    <option value="preparing">Preparing</option>
                                                    <option value="dispatched">Dispatched</option>
                                                    <option value="completed">Completed</option>
                                                </select>
                                            )}
                                        </td>
                                    </tr>
                                ))
                                : (
                                    <tr key={order.id + '-empty'} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">#{order.id.slice(-6)}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{order.customer_name || order.user?.name || 'N/A'}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">₹{order.amount || order.total_amount || 'N/A'}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 max-w-[150px] truncate">{order.billing_address || 'N/A'}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{order.phone || order.contact || 'N/A'}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            <span className={`px-2 py-1 rounded-full text-xs font-bold ${order.payment_method === 'online' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}`}>
                                                {order.payment_method === 'online' ? 'ONLINE' : 'COD'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">N/A</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">N/A</td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`inline-flex px-3 py-1 text-xs font-bold rounded-full ${getStatusColor(order.status)}`}>
                                                {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                            <select
                                                value={order.status}
                                                onChange={e => updateOrderStatus(order.id, e.target.value)}
                                                className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-[#E31E24] focus:border-[#E31E24] bg-white shadow-sm"
                                            >
                                                <option value="preparing">Preparing</option>
                                                <option value="dispatched">Dispatched</option>
                                                <option value="completed">Completed</option>
                                            </select>
                                        </td>
                                    </tr>
                                );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default AdminOrders;
